import { useEffect, useState } from "react";
import { Plus, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { Loading } from "../../components/PageHeader";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";
import { AdminTitle, Pill, ErrorBox } from "./AdminLayout";

const LABELS = {
  manage_products: "Products", manage_prices: "Prices", manage_media: "Media", manage_orders: "Orders",
  manage_users: "Users", manage_roles: "Roles", manage_messages: "Messages", manage_site_content: "Site content", view_analytics: "Analytics",
};

function RoleDialog({ role, all, onClose, onSaved }) {
  const [name, setName] = useState(role?.name || "");
  const [perms, setPerms] = useState(role?.permissions || []);
  const [busy, setBusy] = useState(false);
  const nameOnly = role?.key === "admin";
  const toggle = (p) => setPerms(perms.includes(p) ? perms.filter((x) => x !== p) : [...perms, p]);
  const save = async () => {
    setBusy(true);
    try {
      const body = nameOnly ? { name } : { name, permissions: perms };
      const { data } = role ? await api.patch(`/admin/roles/${role.key}`, body) : await api.post("/admin/roles", body);
      onSaved(data); toast.success(role ? "Role updated" : "Role created"); onClose();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-none" data-testid="role-dialog">
        <DialogTitle className="font-display text-3xl font-normal">{role ? `Edit ${role.name}` : "New role"}</DialogTitle>
        <div><label className="wb-label">Role name</label><input className="wb-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="role-form-name" /></div>
        <div className="grid grid-cols-2 gap-2">
          {all.map((p) => (
            <label key={p} className={`flex items-center gap-2 border wb-line p-2.5 text-sm ${nameOnly ? "opacity-50" : "cursor-pointer"}`}>
              <input type="checkbox" className="accent-[#D4AF37]" checked={nameOnly || perms.includes(p)} disabled={nameOnly} onChange={() => toggle(p)} data-testid={`role-perm-${p}`} />
              {LABELS[p]} <span className="ml-auto font-mono text-[0.55rem] muted">{p}</span>
            </label>
          ))}
        </div>
        {nameOnly && <p className="text-xs muted">Admin always has every permission; only its name can change.</p>}
        <button onClick={save} disabled={busy || name.trim().length < 2} className="btn-gold w-full" data-testid="role-form-save">{busy ? "Saving…" : "Save role"}</button>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminRoles() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [dialog, setDialog] = useState(null);
  const load = () => api.get("/admin/roles").then((r) => setData(r.data)).catch((e) => setErr(errMsg(e)));
  useEffect(() => { load(); }, []);
  const del = async (r) => {
    try {
      await api.delete(`/admin/roles/${r.key}`);
      toast.success("Role deleted"); load();
    } catch (e) {
      if (e?.response?.status !== 409) return toast.error(errMsg(e));
      if (!window.confirm(`${errMsg(e)}\n\nReassign them to Customer and delete “${r.name}”?`)) return;
      try { const { data: d } = await api.delete(`/admin/roles/${r.key}`, { params: { reassign: true } }); toast.success(`Role deleted; ${d.reassigned} user(s) moved to Customer`); load(); }
      catch (e2) { toast.error(errMsg(e2)); }
    }
  };
  if (err) return <ErrorBox msg={err} />;
  if (!data) return <Loading />;
  return (
    <div data-testid="admin-roles">
      <AdminTitle eyebrow="Access" title="Roles & permissions">
        <button className="btn-gold" onClick={() => setDialog("new")} data-testid="role-create-button"><Plus size={14} /> New role</button>
      </AdminTitle>
      <div className="grid gap-4 lg:grid-cols-2">
        {data.roles.map((r) => (
          <div key={r.key} className="wb-card p-6" data-testid={`role-card-${r.key}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 font-display text-2xl">{r.name} {r.locked && <Lock size={14} className="muted" />}</div>
                <div className="mt-1 font-mono text-xs muted">{r.key} · {r.user_count} user{r.user_count === 1 ? "" : "s"}</div>
              </div>
              {r.builtin && <Pill tone="muted">Built-in</Pill>}
            </div>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {r.permissions.length ? r.permissions.map((p) => <Pill key={p} tone="gold">{LABELS[p]}</Pill>) : <span className="text-xs muted">No admin access</span>}
            </div>
            <div className="mt-5 flex gap-2">
              {!r.locked && <button className="btn-ghost !py-2" onClick={() => setDialog(r)} data-testid={`role-edit-${r.key}`}>Edit</button>}
              {!r.builtin && <button className="btn-ghost !py-2 hover:!text-red-400" onClick={() => del(r)} data-testid={`role-delete-${r.key}`}>Delete</button>}
            </div>
          </div>
        ))}
      </div>
      {dialog && <RoleDialog role={dialog === "new" ? null : dialog} all={data.all_permissions} onClose={() => setDialog(null)} onSaved={() => load()} />}
    </div>
  );
}
